import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, HeartHandshake, Sparkles, Award, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, CLINIC } from "@/lib/api";
import GoogleReviewBadge from "@/components/GoogleReviewBadge";

const trust = [
  { icon: ShieldCheck, title: "Sterile & Safe", text: "Autoclaved instruments, disposable kits" },
  { icon: HeartHandshake, title: "Pain-Free Care", text: "Anxiety-friendly single-sitting treatments" },
  { icon: Sparkles, title: "Premium Materials", text: "German & Swiss dental brands only" },
  { icon: Award, title: "12+ Years Expertise", text: "Trusted by 8,000+ happy patients" },
];

const fallbackTestimonials = [
  { name: "Priya Sharma", message: "Dr. Deepika made my daughter's first dental visit a breeze. Genuine, gentle and highly professional.", rating: 5, treatment: "Kids Dentistry" },
  { name: "Rakesh Meena", message: "Painless root canal in one sitting — I could not believe it. The clinic feels like a spa, not a hospital.", rating: 5, treatment: "Root Canal" },
  { name: "Aisha Khan", message: "Got my teeth whitening done. The result is amazing and the team is very warm. Highly recommend.", rating: 5, treatment: "Teeth Whitening" },
];

export default function Home() {
  const [treatments, setTreatments] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    document.title = "Manasvi Dental Clinic — Best Dentist in Kota | Dr. Deepika Charan";
    api.get("/treatments").then(r => setTreatments(r.data.slice(0, 6))).catch(() => {});
    api.get("/reviews").then(r => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const shownTestimonials = reviews.length ? reviews : fallbackTestimonials;

  return (
    <div className="anim-fade-up">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-primary bg-primary/10 px-4 py-1.5 rounded-full mb-6" data-testid="hero-badge">
              Premium Dental Care • Kota
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              A smile you&apos;ll <em className="text-primary">love</em> to share.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg">
              Modern, painless dentistry led by <strong className="text-foreground">Dr. Deepika Charan</strong> — BDS, MIDA, Certified Implantologist &amp; Orthodontist.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book"><Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-8 h-12" data-testid="hero-book-btn">Book Appointment <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              <Link to="/services"><Button variant="outline" size="lg" className="rounded-full border-foreground/20 h-12 px-8" data-testid="hero-services-btn">Explore Treatments</Button></Link>
            </div>
            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (<div key={i} className="w-9 h-9 rounded-full border-2 border-background bg-secondary" style={{backgroundImage:`url(https://i.pravatar.cc/60?img=${i+10})`, backgroundSize:'cover'}} />))}
              </div>
              <div>
                <div className="flex gap-0.5">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-accent text-accent" />)}</div>
                <div className="text-xs text-muted-foreground mt-1">Trusted by <strong className="text-foreground">8,000+</strong> patients</div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/reviews"><GoogleReviewBadge rating={5.0} count={194} variant="compact" /></Link>
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[5/4] shadow-xl">
              <img src="https://images.pexels.com/photos/14458273/pexels-photo-14458273.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=940" alt="Dental care at Manasvi" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-card p-5 rounded-2xl shadow-lg border border-border max-w-[240px]">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Today</div>
              <div className="font-serif text-2xl mt-1">Open Now</div>
              <div className="text-xs text-muted-foreground mt-1">{CLINIC.hours}</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-muted/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {trust.map((t, i) => (
            <div key={i} className="flex gap-4" data-testid={`trust-${i}`}>
              <div className="w-11 h-11 rounded-full bg-primary/15 grid place-items-center shrink-0"><t.icon className="w-5 h-5 text-primary" /></div>
              <div>
                <div className="font-serif text-lg">{t.title}</div>
                <div className="text-sm text-muted-foreground">{t.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Our Treatments</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Complete dental care,<br /> under one roof.</h2>
          </div>
          <Link to="/services"><Button variant="ghost" data-testid="home-view-all-services">View all <ArrowRight className="ml-1 w-4 h-4" /></Button></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {treatments.map(t => (
            <div key={t.id} className="bg-card border border-border rounded-2xl p-8 transition-all hover:-translate-y-1 hover:shadow-md" data-testid={`service-card-${t.slug}`}>
              <div className="w-12 h-12 rounded-xl bg-accent/20 grid place-items-center mb-5"><Sparkles className="w-5 h-5 text-accent-foreground" /></div>
              <h3 className="font-serif text-2xl mb-2">{t.name}</h3>
              <p className="text-sm text-muted-foreground mb-5">{t.description}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-primary font-medium">{t.price}</span>
                <span className="text-muted-foreground">{t.duration}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DOCTOR SECTION */}
      <section className="bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/5] max-w-md">
              <img src="https://images.unsplash.com/photo-1683348758606-860c720fda9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxmZW1hbGUlMjBpbmRpYW4lMjBkZW50aXN0JTIwcHJvZmVzc2lvbmFsJTIwZnJpZW5kbHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODU2NDExNjh8MA&ixlib=rb-4.1.0&q=85" alt="Dr. Deepika Charan" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Meet Your Dentist</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Dr. Deepika Charan</h2>
            <div className="text-muted-foreground mt-2">{CLINIC.doctorCred}</div>
            <p className="mt-6 text-lg text-foreground/90 leading-relaxed">
              With hands-on expertise in implantology and orthodontics, Dr. Deepika believes every patient deserves treatment that&apos;s precise, gentle, and truly personalised.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              A certified implantologist and orthodontist, she has trained in advanced endodontics and continues to bring the latest global techniques to Kota — right at Manasvi Dental Clinic.
            </p>
            <div className="mt-6">
              <Link to="/doctor"><Button variant="outline" className="rounded-full" data-testid="home-doctor-learn-more">Read Full Profile <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="font-serif text-3xl text-primary">12+</div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Years</div></div>
              <div><div className="font-serif text-3xl text-primary">8k+</div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Patients</div></div>
              <div><div className="font-serif text-3xl text-primary">30+</div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Awards</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Words from Our Patients</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight max-w-2xl">Smiles designed with heart.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {shownTestimonials.map((t, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-8 relative" data-testid={`testimonial-${i}`}>
              <Quote className="w-6 h-6 text-accent absolute top-6 right-6 opacity-40" />
              <div className="flex gap-0.5 mb-4">{[...Array(t.rating)].map((_,j)=><Star key={j} className="w-4 h-4 fill-accent text-accent" />)}</div>
              <p className="text-foreground/90 leading-relaxed">&ldquo;{t.message}&rdquo;</p>
              <div className="mt-6 pt-4 border-t border-border">
                <div className="font-serif text-lg">{t.name}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{t.treatment || "Kota, Rajasthan"}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/reviews"><Button variant="outline" className="rounded-full" data-testid="home-all-reviews">Read all reviews <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Ready for your healthiest smile yet?</h2>
            <p className="mt-4 text-primary-foreground/85 max-w-xl">Book your consultation with Dr. Deepika Charan and experience dentistry that actually feels good.</p>
          </div>
          <div className="lg:justify-self-end flex gap-3">
            <Link to="/book"><Button size="lg" className="rounded-full bg-background text-foreground hover:bg-background/90 h-12 px-8" data-testid="cta-book">Book Now</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="rounded-full border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-8" data-testid="cta-contact">Contact Us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
